import React, {useCallback, useEffect, useState} from 'react';
import {AppField} from "../ui/AppField";
import {AppButton} from "../ui/AppButton";
import {Routes} from "../../constants/Routes";
import {makeStyles} from "@mui/styles";
import {useFormik} from "formik";
import {showError} from "../../utils/NotificationUtils";
import {useI18n} from "../../i18n/I18nContext";
import {useIntegrationsContext} from "../../api/integrations/IntegrationsContext";
import {useHistory, useParams} from "react-router";
import {object, string} from "yup";
import {IntegrationItemProps} from "../../api/integrations/IntegrationsDTO";
import {Loader} from "../ui/Loader";
import {Card} from "@mui/material";
import {pathParams} from "path-params";

const validationSchema = (translate: any) => object({
    name: string().required(translate("REQUIRED")),
    description: string().max(140).required(translate("REQUIRED")),
    logoUrl: string().url().required(translate("REQUIRED")),
    homePage: string(),
});

const useStyles = makeStyles(() => ({
    root: {
        padding: 24,
    },
    fieldRoot: {
        marginBottom: 24
    },
    cardRoot: {
        padding: 24,
        borderRadius: "16px !important" as any
    },
    formTitle: {
        fontSize: 18,
        lineHeight: "21px",
        fontWeight: 700,
        color: "#000",
        marginBottom: 16
    }
}));

export function IntegrationEdit() {
    const classes = useStyles();
    const {translate} = useI18n();
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const {IntegrationsApi} = useIntegrationsContext();
    const params: any = useParams();
    const history = useHistory();
    const [data, setData] = useState<IntegrationItemProps>();

    useEffect(() => {
        IntegrationsApi.getIntegrationById({params: {id: params.id}}).then(res => {
            setData(res.payload);
            setLoading(false)
        }).catch(err => {
            showError(err);
            setLoading(false);
        })
    }, [IntegrationsApi, params]);

    const submitHandler = useCallback((values) => {
        setSubmitting(true);
        IntegrationsApi.updateIntegration({
            data: {
                description: values.description,
                homePage: values.homePage,
                name: values.name,
                id: Number(params.id),
                logoUrl: values.logoUrl
            }
        }).then(res => {
            setSubmitting(false);
            window.open("/integration/editor/" + res.payload.id + "/authentication", "_self")
        }).catch(err => {
            setSubmitting(false);
            showError(err);
        })
    }, [IntegrationsApi, history, params]);

    const formik = useFormik({
        initialValues: {
            name: data?.name,
            homePage: data?.homePage,
            description: data?.description,
            logoUrl: data?.logoUrl,
        },
        validationSchema: validationSchema(translate),
        onSubmit: (values) => submitHandler(values),
        enableReinitialize: true
    });
    return (
        <div className={classes.root}>
            {loading ? (
                <Loader/>
            ) : (
               data && (
                <Card className={classes.cardRoot}>
                    <h2 className={classes.formTitle}>Update Integration</h2>
                    <form onSubmit={formik.handleSubmit}>
                        <div className={classes.fieldRoot}>

                            <AppField
                                fullWidth
                                required={true}
                                variant="outlined"
                                labelText="Name"
                                name="name"
                                id="name"
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                helpDesc="The name of your app as it should display in Tact."
                            />
                        </div>
                        <div className={classes.fieldRoot}>
                            <AppField
                                fullWidth
                                required={true}
                                labelText="Description"
                                name="description"
                                variant="outlined"
                                id="description"
                                limit={140}
                                valueLength={formik.values.description?.length}
                                error={
                                    formik.touched.description && Boolean(formik.errors.description)
                                }
                                helperText={formik.touched.description && formik.errors.description}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                helpDesc='A sentence describing your app in 140 characters or less, e.g. "Trello is a team collaboration tool to organize tasks and keep projects on track."'
                            />
                        </div>
                        <div className={classes.fieldRoot}>
                            <AppField
                                fullWidth
                                required={true}
                                labelText="Logo Url"
                                name="logoUrl"
                                variant="outlined"
                                id="logoUrl"
                                error={formik.touched.logoUrl && Boolean(formik.errors.logoUrl)}
                                helperText={formik.touched.logoUrl && formik.errors.logoUrl}
                                value={formik.values.logoUrl}
                                onChange={formik.handleChange}
                                helpDesc="The Logo URL of your app."
                            />
                        </div>

                        <div className={classes.fieldRoot}>
                            <AppField
                                fullWidth
                                labelText="Home Page Url"
                                name="homePage"
                                variant="outlined"
                                id="homePage"
                                error={formik.touched.homePage && Boolean(formik.errors.homePage)}
                                helperText={formik.touched.homePage && formik.errors.homePage}
                                value={formik.values.homePage}
                                onChange={formik.handleChange}
                                helpDesc="The homepage URL of your app."
                            />
                        </div>
                        <AppButton background="#F2F2F2" sx={{mr: 2}} textColor="#777777"
                                   onClick={() => history.replace({pathname: pathParams(Routes.IntegrationEditorAuth, {id: params.id})})}>Cancel</AppButton>
                        <AppButton type="submit" disabled={submitting} loading={submitting}>Update</AppButton>
                    </form>
                </Card>
               )
            )}
        </div>
    );
}

